<template>
  <div v-if="isVisibleSource || isVisibleTarget" class="box" :style="styleMessageContainer">
    <div class="bubble-container">
      <div class="profile-wrapper">
        <ScreenMessageProfile />
        <div class="wrapper-info">
          <h4 class="medium chat-info">
            {{ mappingProfileThis?.nickname }}
          </h4>
          <p class="medium time">{{ chatMessageScreen.time }}</p>
        </div>
      </div>
      <ScreenMessageText class="bubble-frame" :chat-message-screen :is-visible-source :is-visible-target />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, provide, Ref, ref, toRef, watch } from 'vue';

import { storeToRefs } from 'pinia';

import ScreenMessageProfile from '@/components/domains/chat/chat-screen/ScreenMessageProfile.vue';
import ScreenMessageText from '@/components/domains/chat/chat-screen/ScreenMessageText.vue';
import { useMessageBubbleCommon } from '@/composables/domains/chat/useMessageBubbleCommon';
import { useProfile } from '@/composables/domains/chat/useProfile';
import { useAudioStore } from '@/stores/audio';
import { useUserStore } from '@/stores/user';
import { ChatMessage, MappingProfile } from '@/types/socket';
import {
  ij_chatMessageScreen,
  ij_isLastIsOther,
  ij_isLastMessage,
  ij_mappingProfile,
  ij_mappingProfileThis,
  ij_setEmptyBubbleScreen,
} from '@/utils/injectionKeys';

const audioStore = useAudioStore();

const userStore = useUserStore();
const { userPreferences } = storeToRefs(userStore);

const props = defineProps<{
  chatMessageScreen: ChatMessage;
  messageList: Array<ChatMessage>;
}>();

const mappingProfile = inject(ij_mappingProfile) as Ref<MappingProfile>;
const setEmptyBubbleScreen = inject(ij_setEmptyBubbleScreen, (callLogId: string) => {});

const isHidden = ref(false);

const { isEmptyTgt, isEmptySrc, handleChangeReset } = useMessageBubbleCommon({
  chatMessage: toRef(props, 'chatMessageScreen'),
  isHidden,
  setEmptyBubble: setEmptyBubbleScreen,
});

const { getColorProfile, getInitials } = useProfile();

// 메세지 스타일 반환
const styleMessageContainer = computed(() => {
  // 기본 maxWidth는 그대로 유지
  const style: Record<string, string> = {
    maxWidth: dynamicMaxWidth.value,
  };
  style['--font-multiplier'] = String(fontMultiplier.value);
  return style;
});

// 원문 보임 여부
const isVisibleSource = computed(() => {
  const isSingleLanguage = audioStore?.isSingleLanguage; // 단일 언어인가
  const isActiveMyLanguageOnly = userStore.userPreferences.isActiveMyLanguageOnly; // 나의 언어만 보기 활성화 여부
  return !isEmptySrc.value && (isSingleLanguage || !isActiveMyLanguageOnly);
});

// 번역문 보임 여부
const isVisibleTarget = computed(() => {
  return !isEmptyTgt.value;
});

// 이 메제시의 발화자 프로필
const mappingProfileThis = computed(() => {
  const { nickname, senderId } = props.chatMessageScreen;
  if (nickname) {
    return {
      senderId,
      nickname,
      initials: getInitials(nickname),
      color: getColorProfile(senderId),
      isPartnerPerson: false,
    };
  }
  return (
    mappingProfile.value[senderId] ?? {
      senderId,
      nickname: '',
      initials: '',
      color: getColorProfile(senderId),
      isPartnerPerson: false,
    }
  );
});

// 상대 발화 중 이 메세지가 마지막인지
const isLastIsOther = computed(() => {
  const messageLastIsOwn = props.messageList.findLast(
    (item) => item.senderId === props.chatMessageScreen?.senderId // 내가 아닌 상대방 중에서 이 메세지의 발화자의 마지막 메세지
  );
  return messageLastIsOwn!.callLogId === props.chatMessageScreen?.callLogId;
});

// 상대 발화 중 이 메세지가 마지막인지
const isLastMessage = computed(() => props.messageList.at(-1)!.callLogId === props.chatMessageScreen?.callLogId);

// 글씨 크기에 따른 max-width조절
const dynamicMaxWidth = computed(() => {
  return `${userPreferences.value.screenMessageWidth}%`;
});

// 글자 크기 배율
const fontMultiplier = computed(() => userStore.userPreferences.textSizeMultiplier ?? 1);

watch(
  () => props.chatMessageScreen.reset,
  (resetNew) => {
    handleChangeReset(resetNew as '0' | '1');
  },
  { immediate: true }
);

provide(ij_chatMessageScreen, props.chatMessageScreen);
provide(ij_mappingProfileThis, mappingProfileThis);
provide(ij_isLastIsOther, isLastIsOther);
provide(ij_isLastMessage, isLastMessage);
</script>

<style scoped>
.hidden {
  visibility: hidden;
}

.box {
  margin: 0 auto;
  width: 100%;
  display: flex;
  padding: 1.25rem 1.25rem 0 1.25rem;
}

.bubble-container {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  height: fit-content;
  z-index: 1;
}

.profile-wrapper {
  display: flex;
  width: 100%;
  flex-direction: row;
  align-items: center;
}

.wrapper-info {
  display: flex;
  gap: 0.5rem;
  width: 100%;
  align-items: center;
}

.chat-info {
  margin-left: 0.5rem;
  color: var(--color-white);
  font-size: calc(var(--font-size-h4) * var(--font-multiplier, 1));
}

.time {
  font-size: calc(var(--font-size-psmall) * var(--font-multiplier, 1));
  line-height: 150%;
  color: var(--color-gray-80);
  margin-top: 0.25rem;
}

.bubble-frame {
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 0.75rem;
}

.nickname {
  position: relative;
  width: fit-content;
  white-space: nowrap;
}
</style>
